import { InboundChange } from "@/models/legislation/metadata/ConsolidationMetadata";
import { TimelineItem } from "@/models/legislation/Timeline";
import { CONSOLIDATION_NOT_FOUND_MESSAGE } from "@/lib/consolidationMetadata";
import { flattenTimeline } from "@/lib/diffViewHtml";
import {
  isChangelawDocumentType,
  isTimelineAmendmentAtPosition,
  scopeInboundChangesAfterAnchorOnTimeline,
} from "@/lib/timelineUniverseParams";
import { isConsolidationAnchorType } from "@/utils/legislationDocumentTypeUtils";

export type TimelineCompareFetchKind =
  | { kind: "silver" }
  | { kind: "consolidation"; changeLaw: string }
  | { kind: "embedded" };

export type TimelineCompareSnapshot = {
  accessionNumber: string;
  fetch: TimelineCompareFetchKind;
};

/** True for a consolidation window anchor timeline node (LBK 30 or BEK 60). */
export const isConsolidationAnchorTimelineItem = (item: TimelineItem): boolean =>
  isConsolidationAnchorType(item.documentTypeId);

export const findInboundByAccession = (
  inbound: ReadonlyArray<InboundChange>,
  accession: string
): InboundChange | undefined =>
  inbound.find((entry) => entry.changelawAccession === accession);

export const timelineItemToCompareTargetInfo = (
  item: TimelineItem,
  accessionNumber: string
) => ({
  accessionNumber,
  title: item.title,
  shortName: item.shortName,
  year: item.year,
  signatureDate: item.signatureDate,
});

export type BuildTimelineCompareSnapshotsOptions = {
  inboundChanges: InboundChange[];
  currentExternalId: string | undefined;
  isConsolidatedRender: boolean;
};

/**
 * Maps each timeline node in a compare range to a fetch strategy:
 * - inbound changelaw → our consolidation render-by-change-law at the anchor
 * - anchor (LBK/BEK) / other nodes → Retsinformation silver HTML
 * - current anchor while viewing consolidated text → already-loaded HTML
 */
export const buildTimelineCompareSnapshots = (
  rangeItems: TimelineItem[],
  options: BuildTimelineCompareSnapshotsOptions
): TimelineCompareSnapshot[] =>
  rangeItems.reduce<TimelineCompareSnapshot[]>((snapshots, item, index) => {
    const accessionNumber = item.accessionNumber?.trim();
    if (!accessionNumber) return snapshots;

    const isLastInRange = index === rangeItems.length - 1;
    const inboundEntry = findInboundByAccession(
      options.inboundChanges,
      accessionNumber
    );

    if (
      isLastInRange &&
      options.isConsolidatedRender &&
      accessionNumber === options.currentExternalId
    ) {
      snapshots.push({ accessionNumber, fetch: { kind: "embedded" } });
      return snapshots;
    }

    if (inboundEntry) {
      snapshots.push({
        accessionNumber,
        fetch: { kind: "consolidation", changeLaw: accessionNumber },
      });
      return snapshots;
    }

    snapshots.push({ accessionNumber, fetch: { kind: "silver" } });
    return snapshots;
  }, []);

/**
 * Inbound changelaws whose consolidation index falls strictly between the
 * inbound endpoints of the range but which are absent as timeline nodes.
 */
export const findSkippedInboundConsolidationsInRange = (
  rangeItems: TimelineItem[],
  inboundChanges: InboundChange[]
): InboundChange[] => {
  if (rangeItems.length < 2 || inboundChanges.length === 0) return [];

  const accessionsInRange = new Set(
    rangeItems
      .map((item) => item.accessionNumber)
      .filter((accession): accession is string => Boolean(accession))
  );

  const inboundOnRange = inboundChanges.filter((entry) =>
    accessionsInRange.has(entry.changelawAccession)
  );
  if (inboundOnRange.length === 0) return [];

  const minIndex = Math.min(...inboundOnRange.map((entry) => entry.timelineIndex));
  const maxIndex = Math.max(...inboundOnRange.map((entry) => entry.timelineIndex));
  if (minIndex >= maxIndex) return [];

  return inboundChanges.filter(
    (entry) =>
      entry.timelineIndex > minIndex &&
      entry.timelineIndex < maxIndex &&
      !accessionsInRange.has(entry.changelawAccession)
  );
};

export const validateTimelineComparisonRange = (
  rangeItems: TimelineItem[],
  inboundChanges: InboundChange[]
): string | null => {
  if (rangeItems.length < 2) {
    return "Vælg en ældre version på tidslinjen for at sammenligne.";
  }

  const skipped = findSkippedInboundConsolidationsInRange(
    rangeItems,
    inboundChanges
  );
  if (skipped.length > 0) {
    const label = skipped[0]?.changelawAccession ?? "ukendt";
    return `Ændringsdokumentet ${label} mangler konsolidering mellem de valgte punkter.`;
  }

  return null;
};

/**
 * Accessions eligible as compare endpoints within a single consolidation window:
 * the anchor (LBK or BEK) itself plus the consolidations of its inbound changelaws
 * (scoped on the timeline so we never cross into another consolidation window).
 */
export const getCompareWindowAccessions = (
  timelineItems: TimelineItem[] | undefined,
  anchorLawAccession: string | undefined,
  inboundChanges: ReadonlyArray<InboundChange> | undefined
): Set<string> => {
  const accessions = new Set<string>();
  const anchor = anchorLawAccession?.trim();
  if (!anchor) return accessions;

  accessions.add(anchor);

  const scopedInbound = scopeInboundChangesAfterAnchorOnTimeline(
    timelineItems,
    anchor,
    inboundChanges
  );
  for (const entry of scopedInbound) {
    if (entry.changelawAccession) {
      accessions.add(entry.changelawAccession);
    }
  }

  return accessions;
};

/** True when the timeline has at least one older node to compare against the current document. */
export const isTimelineComparisonAvailable = (
  flatItems: TimelineItem[],
  currentAccession: string | undefined
): boolean => {
  if (!currentAccession?.trim() || flatItems.length < 2) return false;
  const currentIdx = flatItems.findIndex(
    (item) => item.accessionNumber === currentAccession
  );
  return currentIdx > 0;
};

/**
 * True when consolidation compare should be offered: either an older timeline
 * node exists, or the anchor LBK window has another compare endpoint (e.g.
 * inbound consolidations while the anchor sits at timeline index 0).
 */
export const isConsolidationTimelineComparisonAvailable = (
  timelineItems: TimelineItem[] | undefined,
  anchorLawAccession: string | undefined,
  inboundChanges: ReadonlyArray<InboundChange> | undefined,
  currentAccession: string | undefined
): boolean => {
  const current = currentAccession?.trim();
  if (!current) return false;

  const flatItems = timelineItems ? flattenTimeline(timelineItems) : [];
  if (isTimelineComparisonAvailable(flatItems, current)) {
    return true;
  }

  const compareWindow = getCompareWindowAccessions(
    timelineItems,
    anchorLawAccession,
    inboundChanges
  );
  if (compareWindow.size < 2 || !compareWindow.has(current)) return false;

  for (const accession of compareWindow) {
    if (accession !== current) return true;
  }
  return false;
};

export const isChangelawTimelineItem = (item: TimelineItem): boolean =>
  isChangelawDocumentType(item.documentTypeId);

export const isTimelineAmendmentTimelineItem = (
  item: TimelineItem,
  flatTimelineIndex: number
): boolean => isTimelineAmendmentAtPosition(item.documentTypeId, flatTimelineIndex);

export const isConsolidationNotActivatedError = (error: unknown): boolean =>
  error instanceof Error && error.message === CONSOLIDATION_NOT_FOUND_MESSAGE;

export type CompareSnapshotQueryState = {
  data: unknown;
  isError: boolean;
  error: unknown;
};

/**
 * Resolves processed HTML for a compare snapshot. When a changelaw consolidation
 * is not yet activated (render-by-change-law 404), carries forward the nearest
 * earlier snapshot so compare can proceed across pending consolidations.
 */
export const resolveCompareSnapshotHtml = (
  index: number,
  snapshots: TimelineCompareSnapshot[],
  queryStates: CompareSnapshotQueryState[],
  embeddedHtml: string
): string | null => {
  let currentIndex = index;
  while (currentIndex >= 0) {
    const snapshot = snapshots[currentIndex];
    if (!snapshot) return null;
    if (snapshot.fetch.kind === "embedded") return embeddedHtml;

    const query = queryStates[currentIndex];
    const data = query?.data;
    if (typeof data === "string" && data.length > 0) return data;

    if (
      snapshot.fetch.kind === "consolidation" &&
      query?.isError &&
      isConsolidationNotActivatedError(query.error)
    ) {
      currentIndex -= 1;
      continue;
    }

    return null;
  }
  return null;
};

export const isCompareSnapshotSettled = (
  snapshot: TimelineCompareSnapshot | undefined,
  query: CompareSnapshotQueryState | undefined
): boolean => {
  if (!snapshot || snapshot.fetch.kind === "embedded") return true;
  if (!query) return false;
  return (
    (typeof query.data === "string" && query.data.length > 0) ||
    query.isError
  );
};
